import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Box,
  IconButton,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import dico from "../../../util/src/language/dico";
import ColorPicker from "./ColorPicker";
import {
  EDITOR_PICKER_POPOVER_Z_INDEX,
  editorPickerSelectMenuProps,
} from "./editorPickerPopoverZIndex";

type TextShadowLayer = {
  x: number;
  y: number;
  blur: number;
  color: string;
};

export type TextShadowPickerProps = {
  value: string;
  disabled?: boolean;
  onChange: (nextValue: string) => void;
};

const DEFAULT_LAYER: TextShadowLayer = {
  x: 0,
  y: 0,
  blur: 0,
  color: "#00000000",
};

const parseLayer = (raw: string): TextShadowLayer => {
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "none") return { ...DEFAULT_LAYER };

  const numbers: number[] = [];
  let colorStart = -1;

  const regex = /(-?[0-9]*\.?[0-9]+)px/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(trimmed)) !== null) {
    const token = match[1];
    if (token) {
      numbers.push(Number.parseFloat(token));
    }
    colorStart = match.index + match[0].length;
  }

  const color =
    colorStart >= 0 ? trimmed.slice(colorStart).trim() : "#00000073";

  return {
    x: numbers[0] ?? 0,
    y: numbers[1] ?? 0,
    blur: numbers[2] ?? 0,
    color: color || "#00000073",
  };
};

const parseLayers = (value: string): TextShadowLayer[] => {
  const trimmed = value.trim();
  if (!trimmed || trimmed === "none") return [{ ...DEFAULT_LAYER }];

  const parts: string[] = [];
  let depth = 0;
  let current = "";

  for (const char of trimmed) {
    if (char === "(") depth++;
    else if (char === ")") depth--;
    else if (char === "," && depth === 0) {
      parts.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  if (current.trim()) parts.push(current.trim());

  const layers = parts.map(parseLayer);
  return layers.length > 0 ? layers : [{ ...DEFAULT_LAYER }];
};

const stringifyLayer = (layer: TextShadowLayer): string =>
  `${layer.x}px ${layer.y}px ${layer.blur}px ${layer.color}`;

const stringifyLayers = (layers: TextShadowLayer[]): string => {
  if (layers.length === 0) return "none";
  return layers.map(stringifyLayer).join(", ");
};

const DEFAULT_TEXT_SHADOW_VALUE = "none";

/**
 * Edits a CSS `text-shadow` value with multi-layer support,
 * offsets, blur and color selection via ColorPicker.
 *
 * @param value - Current CSS text-shadow string.
 * @param disabled - Disables interaction.
 * @param onChange - Callback with the updated CSS string.
 */
const TextShadowPicker: React.FC<TextShadowPickerProps> = ({
  value,
  disabled = false,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const popupOpen = Boolean(anchorEl);

  const labels =
    (
      dico().licensedPortal?.layoutEditor as {
        textShadowPicker?: Record<string, string>;
      }
    )?.textShadowPicker ?? ({} as Record<string, string>);

  const layers = useMemo(() => parseLayers(value), [value]);

  const emitChange = useCallback(
    (nextLayers: TextShadowLayer[]) => {
      onChange(stringifyLayers(nextLayers));
    },
    [onChange],
  );

  const updateActiveLayer = useCallback(
    (partial: Partial<TextShadowLayer>) => {
      const nextLayers = layers.map((l, i) =>
        i === activeIndex ? { ...l, ...partial } : l,
      );
      emitChange(nextLayers);
    },
    [layers, activeIndex, emitChange],
  );

  const addLayer = useCallback(() => {
    const nextLayers = [...layers, { ...DEFAULT_LAYER }];
    setActiveIndex(nextLayers.length - 1);
    emitChange(nextLayers);
  }, [layers, emitChange]);

  const removeLayer = useCallback(() => {
    if (layers.length <= 1) return;
    const nextLayers = layers.filter((_, i) => i !== activeIndex);
    const nextIndex = Math.min(activeIndex, nextLayers.length - 1);
    setActiveIndex(nextIndex);
    emitChange(nextLayers);
  }, [layers, activeIndex, emitChange]);

  const activeLayer = layers[activeIndex] ?? layers[0] ?? DEFAULT_LAYER;

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={(e) => {
          if (!disabled) setAnchorEl(e.currentTarget as HTMLElement);
        }}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setAnchorEl(e.currentTarget as HTMLElement);
          }
        }}
        sx={{
          border: "1px solid #d8e0eb",
          borderRadius: "8px",
          backgroundColor: "#fbfcfe",
          p: "3px 8px",
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.55 : 1,
          "&:hover": {
            borderColor: disabled ? "#d8e0eb" : "#94a3b8",
          },
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 16,
            color: "#334155",
            textShadow: value || "none",
            userSelect: "none",
          }}
        >
          Abc
        </Typography>
      </Box>

      <Popover
        open={popupOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          root: {
            sx: { zIndex: EDITOR_PICKER_POPOVER_Z_INDEX },
          },
        }}
      >
        <Box
          sx={{
            width: 320,
            background: "#fff",
            borderRadius: "12px",
            p: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, color: "#1f2937" }}
            >
              {labels.title ?? "Text Shadow"}
            </Typography>
            <Tooltip title={labels.reset ?? ""}>
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => {
                    setActiveIndex(0);
                    onChange(DEFAULT_TEXT_SHADOW_VALUE);
                  }}
                >
                  <RestartAltIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box
            sx={{ display: "flex", gap: 1, alignItems: "flex-end", mb: 1.5 }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography sx={sectionLabelSx}>
                {labels.layerLabel ?? "Layer"}
              </Typography>
              <Select
                size="small"
                fullWidth
                value={String(activeIndex)}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "+") {
                    addLayer();
                  } else {
                    setActiveIndex(Number(val));
                  }
                }}
                sx={selectSx}
                MenuProps={editorPickerSelectMenuProps}
              >
                {layers.map((_, i) => (
                  <MenuItem key={i} value={String(i)}>
                    {i + 1}
                  </MenuItem>
                ))}
                <MenuItem value="+">+</MenuItem>
              </Select>
            </Box>
            <IconButton
              size="small"
              disabled={layers.length <= 1}
              onClick={removeLayer}
              sx={{
                border: "1px solid #c7d2e0",
                borderRadius: "6px",
                width: 40,
                height: 40,
                color: "#334155",
                flexShrink: 0,
                "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" },
                "&.Mui-disabled": { opacity: 0.45 },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
              mb: 1,
            }}
          >
            <Box>
              <Typography sx={sectionLabelSx}>X (px)</Typography>
              <TextField
                size="small"
                type="number"
                fullWidth
                value={activeLayer.x}
                onChange={(e) =>
                  updateActiveLayer({ x: Number(e.target.value) || 0 })
                }
                sx={inputSx}
              />
            </Box>
            <Box>
              <Typography sx={sectionLabelSx}>Y (px)</Typography>
              <TextField
                size="small"
                type="number"
                fullWidth
                value={activeLayer.y}
                onChange={(e) =>
                  updateActiveLayer({ y: Number(e.target.value) || 0 })
                }
                sx={inputSx}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
              mb: 1.5,
            }}
          >
            <Box>
              <Typography sx={sectionLabelSx}>
                {labels.blur ?? "Blur"} (px)
              </Typography>
              <TextField
                size="small"
                type="number"
                fullWidth
                value={activeLayer.blur}
                onChange={(e) =>
                  updateActiveLayer({
                    blur: Math.max(0, Number(e.target.value) || 0),
                  })
                }
                sx={inputSx}
                slotProps={{ htmlInput: { min: 0 } }}
              />
            </Box>
            <Box>
              <Typography sx={sectionLabelSx}>
                {labels.color ?? "Color"}
              </Typography>
              <ColorPicker
                value={activeLayer.color}
                disabled={disabled}
                onChange={(nextColor) =>
                  updateActiveLayer({ color: nextColor })
                }
              />
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const sectionLabelSx = {
  display: "block",
  fontSize: 12,
  color: "#64748b",
  mb: 0.75,
};

const inputSx = {
  "& .MuiInputBase-root": {
    height: 40,
    borderRadius: "6px",
    "& input": { fontSize: 13 },
  },
};

const selectSx = {
  height: 40,
  borderRadius: "6px",
  fontSize: 13,
};

export default TextShadowPicker;
